import { Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';

const C_ALLOCATED = '#6EE7B7';
const C_USED = '#3B82F6';

@Component({
  selector: 'app-usage-percentage-donut',
  imports: [CommonModule, MatCardModule, TranslateModule],
  templateUrl: './usage-percentage-donut.html',
  styleUrl: './usage-percentage-donut.css',
})
export class UsagePercentageDonutComponent {
  @Input() title: string = '';
  @Input() set totalAllocated(value: number | null | undefined) {
    this._totalAllocated.set(value ?? 0);
  }
  @Input() set usagePercentage(value: number | null | undefined) {
    this._usagePercentage.set(value ?? 0);
  }

  private _totalAllocated = signal<number>(0);
  private _usagePercentage = signal<number>(0);

  readonly C_ALLOCATED = C_ALLOCATED;
  readonly C_USED = C_USED;

  stats = computed(() => {
    const pct = Math.min(100, Math.max(0, this._usagePercentage()));
    const allocated = Math.max(0, this._totalAllocated());

    const allocatedAngle = ((100 - pct) / 100) * 360;

    return {
      pct,
      allocated,
      allocatedTons: this.kgToTons(allocated),
      segments: [
        {
          name: 'Распределено',
          color: C_ALLOCATED,
          percentage: 100 - pct,
          startAngle: 0,
          endAngle: allocatedAngle
        },
        {
          name: 'Использовано',
          color: C_USED,
          percentage: pct,
          startAngle: allocatedAngle,
          endAngle: 360
        }
      ]
    };
  });

  kgToTons(kg: number): string {
    if (kg === 0) return '0';
    const tons = kg / 1000;
    return tons.toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  }

  describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
    let arcAngle = endAngle - startAngle;
    if (arcAngle < 0) arcAngle += 360;

    // Для полного круга или почти полного (≥359.9°) рисуем две дуги
    if (arcAngle >= 359.9) {
      const mid = startAngle + 180;
      const midPoint = this.polarToCartesian(x, y, radius, mid);
      const start = this.polarToCartesian(x, y, radius, startAngle);
      const end = this.polarToCartesian(x, y, radius, endAngle);

      return [
        'M', start.x, start.y,
        'A', radius, radius, 0, 1, 1, midPoint.x, midPoint.y,
        'A', radius, radius, 0, 1, 1, end.x, end.y
      ].join(' ');
    }

    const start = this.polarToCartesian(x, y, radius, startAngle);
    const end = this.polarToCartesian(x, y, radius, endAngle);
    const largeArcFlag = arcAngle > 180 ? '1' : '0';

    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y
    ].join(' ');
  }

  private polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = angleInDegrees * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  }
}
